import { ActualAuctionStatusEnum } from "../../../_common/enum/actualAuctionStatus";
import { AuctionStatusEnum } from "../../../_common/enum/auctionStatus.enum";

export interface AnnualUserRow {
    id: number;
    userId: string;
    userName?: string;
    clientName?: string;
    volumePerRound?: number | null;
    sequenceOrder?: number | null;
    bauRestrictions?: AnnualBauRestrictionRow[];
}

export interface AnnualCatalogueItemRow {
    id?: number;
    lotNo?: number;
    forestCode?: string;
    product?: string;
    district?: string;
    totalVolume?: number;
    price?: number | null;
}

export interface BasketHistoryEntry {
    roundNumber: number;
    itemIds: number[];
    allowance: number;
    used: number;
    remaining: number;
}

export interface AnnualBauRestrictionRow {
    bauCode?: string;
    quantity?: number | null;
}

export interface AuctionAnnualStateResponse {
    auctionId: number;
    currentRoundNumber: number;
    currentCatalogueUserId: number;
    currentCatalogueUserName?: string | null;
    currentCatalogueUserSequenceOrder?: number | null;
    myCatalogueUserId?: number | null;
    myCatalogueUserName?: string | null;
    mySequenceOrder?: number | null;
    myVolumePerRound?: number | null;
    myPositionsUntilTurn?: number | null;
    isRegistered?: boolean;
    maxRounds?: number;
    isLastRound?: boolean;
    lastRoundExtraAllowance?: number;
    myBauRestrictions?: AnnualBauRestrictionRow[];
    /** Lot ids already taken by anyone — no who-took-it info. */
    occupiedCatalogueItemIds?: number[];
    /** Configured turn length in seconds (default 600). */
    timerDurationSeconds?: number;
    /** UTC deadline while running; null when paused/idle. */
    timerEndsAtUtc?: string | null;
    /** Frozen remaining seconds while paused; null when running or full reset. */
    timerRemainingSeconds?: number | null;
}

export interface AuctionAnnualTurnResponse {
    roundNumber: number;
    catalogueUserId: number;
    allowance: number;
    used: number;
    remaining: number;
    itemIds: number[];
}

export interface AnnualTurnHistoryState {
    basketByUserId: Record<number, number[]>;
    basketHistoryByUserId: Record<number, BasketHistoryEntry[]>;
    carryOverByUserId: Record<number, number>;
}

export function getAnnualActualStatus(auctionStatusId: number | null | undefined) {
    if (auctionStatusId === AuctionStatusEnum.Active) return ActualAuctionStatusEnum.Active;
    if (auctionStatusId === AuctionStatusEnum.Paused) return ActualAuctionStatusEnum.Paused;
    if (auctionStatusId === AuctionStatusEnum.Pending) return ActualAuctionStatusEnum.Pending;
    if (auctionStatusId === AuctionStatusEnum.Completed) return ActualAuctionStatusEnum.Completed;
    if (auctionStatusId === AuctionStatusEnum.UserDisconnected) return ActualAuctionStatusEnum.UserDisconnected;
    return ActualAuctionStatusEnum.NotStarted;
}

/** Remaining seconds from server timer anchors (shared by admin + bidder). Never negative. */
export function clampAnnualTimerSeconds(sec: number): number {
    if (!Number.isFinite(sec)) return 0;
    return Math.max(0, Math.floor(sec));
}

export function computeAnnualTimerRemainingSeconds(
    state: Pick<AuctionAnnualStateResponse, "timerDurationSeconds" | "timerEndsAtUtc" | "timerRemainingSeconds"> | null | undefined,
    nowMs: number = Date.now(),
): number {
    const duration = clampAnnualTimerSeconds(Number(state?.timerDurationSeconds ?? 600));
    const endsAt = state?.timerEndsAtUtc;
    if (endsAt) {
        const endsMs = Date.parse(endsAt);
        if (Number.isFinite(endsMs)) return clampAnnualTimerSeconds((endsMs - nowMs) / 1000);
    }
    if (state?.timerRemainingSeconds != null && Number.isFinite(Number(state.timerRemainingSeconds))) {
        return clampAnnualTimerSeconds(Number(state.timerRemainingSeconds));
    }
    return duration;
}

export function sortAnnualCatalogueItems(items: unknown): AnnualCatalogueItemRow[] {
    const safeItems = Array.isArray(items) ? (items as AnnualCatalogueItemRow[]) : [];
    return safeItems
        .slice()
        .sort((a, b) => Number(a?.lotNo ?? 0) - Number(b?.lotNo ?? 0));
}

export function buildAnnualCatalogueItemMap(catalogueItems: AnnualCatalogueItemRow[]) {
    const map = new Map<number, AnnualCatalogueItemRow>();
    catalogueItems.forEach((item) => {
        const id = Number(item.id ?? 0);
        if (id > 0) map.set(id, item);
    });
    return map;
}

export function reduceAnnualTurnHistory(turns: AuctionAnnualTurnResponse[]): AnnualTurnHistoryState {
    const nextBasketByUserId: Record<number, number[]> = {};
    const nextBasketHistoryByUserId: Record<number, BasketHistoryEntry[]> = {};
    const nextCarryOverByUserId: Record<number, number> = {};

    (Array.isArray(turns) ? turns : []).forEach((turn) => {
        const userId = Number(turn.catalogueUserId ?? 0);
        if (!(userId > 0)) return;

        const itemIds = (Array.isArray(turn.itemIds) ? turn.itemIds : [])
            .map((id) => Number(id))
            .filter((id) => id > 0);

        nextBasketByUserId[userId] = [...(nextBasketByUserId[userId] ?? []), ...itemIds];

        const historyEntry: BasketHistoryEntry = {
            roundNumber: Number(turn.roundNumber ?? 0),
            itemIds,
            allowance: Number(turn.allowance ?? 0),
            used: Number(turn.used ?? 0),
            remaining: Number(turn.remaining ?? 0),
        };

        nextBasketHistoryByUserId[userId] = [...(nextBasketHistoryByUserId[userId] ?? []), historyEntry];
        nextCarryOverByUserId[userId] = historyEntry.remaining;
    });

    return {
        basketByUserId: nextBasketByUserId,
        basketHistoryByUserId: nextBasketHistoryByUserId,
        carryOverByUserId: nextCarryOverByUserId,
    };
}
